import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'
import { registerGuard } from './Guard'
import { useAppConfigStore } from '../stores/app-config-store'
import { ToastService } from '../services/toast-service'
import { loginRequest, msalInstance } from '../authConfig'
import { acquireAndProcessIdTokenClaims, getPWRoles } from '../azure/token-utils'
import piniaStores from '@/stores'
import { initializeAppConfigs } from '@/services/init-app-configs'
import { useAuthStore } from '@/stores/auth-store'

// const clientRoutes = clientUriSegments.map((segment: string) => ({
//   path: `/${segment}`,
//   name: segment,
//   component: () => import('../pages/clients/ClientHome.vue'), // Load appropriate component
// }))

export const routes: Array<RouteRecordRaw> = [
  // {
  //   path: '/:pathMatch(.*)*',
  //   redirect: { name: 'dashboard' },
  // },

  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: 'admin',
    path: '/admin',
    component: AppLayout,
    meta: {
      requiresAuth: true,
      requiredRoles: ['ADMIN'],
      clientCodes: ['pw'],
    },
    // redirect: { name: 'dashboard' },
    children: [
      // PW-Admin: clients
      {
        name: 'admin-clients',
        path: 'clients',
        component: () => import('../pages/admin/Clients.vue'),
      },
      // PW-Admin: features
      {
        name: 'admin-features',
        path: 'features',
        component: () => import('../pages/admin/Features.vue'),
      },
      // PW-Admin: subscription-plans
      {
        name: 'admin-subscription-plans',
        path: 'subscription-plans',
        component: () => import('../pages/admin/SubscriptionPlans.vue'),
      },
      // PW-Admin: token usage overview (all clients)
      {
        name: 'admin-token-usage',
        path: 'token-usage',
        component: () => import('../pages/admin/PWAdminTokenUsageOverview.vue'),
      },
      // PW-Admin: platform analytics
      {
        name: 'admin-analytics',
        path: 'analytics',
        component: () => import('../pages/admin/analytics/PWAdminAnalytics.vue'),
      },
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
      },
      {
        name: 'projects',
        path: 'projects',
        component: () => import('../pages/projects/ProjectsPage.vue'),
      },
      {
        name: 'payments',
        path: '/payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/payments/PaymentsPage.vue'),
          },
        ],
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    name: 'error',
    path: '/error',
    component: () => import('../pages/Error.vue'),
  },
  {
    path: '/login',
    redirect: { name: 'login' },
  },
  {
    name: 'app',
    path: '/',
    meta: {
      requiresAuth: true,
    },
    component: AppLayout,
    // component: () => import('../pages/test/Test.vue'),
    children: [
      {
        name: 'home',
        path: '',
        redirect: { name: 'home-page' },
      },
      {
        name: 'home-page',
        path: 'home',
        component: () => import('../pages/home/Home.vue'),
      },
      {
        name: 'test-page',
        path: 'test',
        component: () => import('../pages/test/Test.vue'),
      },
      {
        name: 'settings',
        path: '/settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'profile',
        path: '/profile',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
    ],
  },

  // all client routes
  // ...clientRoutes,

  // check clients if no route matches
  {
    // path: '/:catchAll(.*)*',
    // path: '/:pathMatch(.*)*',
    path: '/:clientCode',
    name: 'check-clients',
    component: () => import('../layouts/ClientAppLayout.vue'),
    meta: {
      requiresAuth: true,
    },
    // component: RouteViewComponent,
    redirect: { name: 'client-home' },
    children: [
      {
        name: 'client-home',
        path: '',
        meta: {
          labAdminAllowWhenOffline: true, // 2025-06-15: false, because we want to redirect the user to client-billing
        },
        component: () => import('../pages/clients/ClientHome.vue'),
        // redirect: { name: 'client-reports' },
      },
      {
        name: 'client-admin',
        path: 'admin',
        meta: {
          requiredRoles: ['ADMIN'],
          labAdminAllowWhenOffline: false,
        },
        component: () => import('../pages/clients/ClientLayout.vue'),
        children: [
          {
            name: 'client-admin-users',
            path: 'users',
            meta: {
              labAdminAllowWhenOffline: true,
            },
            component: () => import('../pages/clients/ClientUsers.vue'),
          },
          {
            name: 'client-admin-user-groups',
            path: 'user-groups',
            meta: {
              labAdminAllowWhenOffline: true,
            },
            component: () => import('../pages/clients/ClientUserGroups.vue'),
          },
          {
            name: 'client-admin-settings',
            path: 'settings',
            component: () => import('../pages/clients/ClientSettings.vue'),
          },
          // subscriptions
          {
            name: 'client-admin-subscriptions',
            path: 'subscriptions',
            meta: {
              requiredRoles: ['ADMIN'],
              clientCodes: ['pw'],
            },
            component: () => import('../pages/clients/ClientSubscriptions.vue'),
          },
          // token usage
          {
            name: 'client-admin-token-usage',
            path: 'token-usage',
            meta: {
              requiredRoles: ['ADMIN'],
              // Remove clientCodes restriction to allow Lab Admin access to their own client's token usage
              // clientCodes: ['pw'],
            },
            component: () => import('../pages/clients/ClientTokenUsage.vue'),
          },
          // analytics
          {
            name: 'client-admin-analytics',
            path: 'analytics',
            meta: {
              requiredRoles: ['ADMIN'],
              requiredFeature: 'basicAnalytics',
            },
            component: () => import('../pages/clients/ClientAnalytics.vue'),
          },
        ],
      },
      {
        name: 'client-reports',
        path: 'reports',
        component: () => import('../pages/clients/ClientReports.vue'),
        meta: {
          labAdminAllowWhenOffline: false,
        },
      },
      {
        name: 'client-pricing-plans',
        path: 'pricing-plans',
        meta: {
          requiredRoles: ['ADMIN'],
          labAdminAllowWhenOffline: true,
        },
        component: () => import('../pages/pricing-plans/PricingPlans.vue'),
      },
      {
        name: 'client-billing',
        path: 'billing',
        meta: {
          requiredRoles: ['ADMIN'],
          labAdminAllowWhenOffline: true,
        },
        component: () => import('../pages/billing/BillingPage.vue'),
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to, from, next) => {
  // if the route is error, bypass the guard
  if (to.name === 'error') {
    // log entry
    console.log('Bypassing guard for error route:', to.name)
    next()
    return
  }

  // Auth redirect is handled in src/router/Guard.ts so we can render a branded pre-login page.
  // Keep this hook focused on bootstrapping public app configs.

  const appConfigStore = useAppConfigStore(piniaStores)
  // Check if CLIENT_URL_SEGMENT_CLIENT_DATA_MAP is missing or empty
  if (
    !appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP ||
    Object.keys(appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP).length === 0
  ) {
    // log entry
    console.log('CLIENT_URL_SEGMENT_CLIENT_DATA_MAP is missing or empty, fetching app configs...')

    try {
      await initializeAppConfigs(piniaStores, msalInstance)
    } catch (e) {
      // If fetch fails, go to error page
      // log error
      console.error('Failed to fetch app configs:', e)

      // wait 3 seconds before redirecting to error page
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve(next({ name: 'error', query: { type: 'network', message: 'Failed to load app configs' } }))
        }, 3000)
      })
      return
      // return next({ name: 'error', query: { type: 'network', message: 'Failed to load app configs' } })
    }
    // Check again after fetch
    // log entry
    console.log('CLIENT_URL_SEGMENT_CLIENT_DATA_MAP after fetch:', appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP)
    if (
      !appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP ||
      Object.keys(appConfigStore.CLIENT_URL_SEGMENT_CLIENT_DATA_MAP).length === 0
    ) {
      // return next({ name: 'error', query: { type: 'server', message: 'App config missing after reload' } })
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve(next({ name: 'error', query: { type: 'server', message: 'App config missing after reload' } }))
        }, 3000)
      })
      return
    }
  }
  next()
})

registerGuard(router)

export default router
